import {Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter} from '@angular/core';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { NgClass } from '@angular/common';
import { Subscription, } from 'rxjs';
import { ApplicationDialogService } from '../application-dialog-component/application-dialog.service';
import { PortalHotToastService } from '../portal-hot-toast-component/hot-toast.service';
import { DuplicatedApplication, displayApplicationDateTime, getCompanyName, getLoanAmount, getAssetDescription } from '@portal-workspace/grow-shared-library';
import { LooseCurrencyPipe } from '../../pipes/loose-currency.pipe';
import { FlexModule } from '@angular/flex-layout';
import { CustomContentLoaderComponent } from "../custom-content-loader-component/custom-content-loader.component";
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { loadingFor } from '@ngneat/loadoff';
import { DatePipe, AsyncPipe, } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import moment from 'moment';
import { TagBoxComponent } from '../message-box/tag-box.component';

@Component({
    selector: 'duplicated-applications',
    templateUrl: './duplicated-applications.component.html',
    styleUrls: ['./duplicated-applications.component.scss'],
    standalone: true,
    imports: [
      MatTableModule, 
      NgClass, 
      ExtendedModule, 
      FlexModule,
      CustomContentLoaderComponent,
      MatSortModule,
      DatePipe,
      AsyncPipe,
      MatButtonModule,
      MatInputModule,
      MatFormFieldModule,
      ReactiveFormsModule,
      TagBoxComponent,
      LooseCurrencyPipe,
    ]
})
export class DuplicatedApplicationsComponent implements OnInit, OnChanges {
  @Input({required: true}) duplicatedApplications!: DuplicatedApplication[];
  @Output() clickApplicationEvent = new EventEmitter<DuplicatedApplication>;

  subscriptions: Subscription[] = [];
  moment = moment;
  displayApplicationDateTime = displayApplicationDateTime;
  formControlSearch!: FormControl<string | null>;

  total = 0;
  filter = '';
  loader = loadingFor('tableLoading');
  sorts: { prop: string, dir: 'asc' | 'desc' } | null = null;
  dataSource: DuplicatedApplication[] = [];
  displayedData: DuplicatedApplication[] = [];
  columnsToDisplay = ['application', 'entityName', 'stage', 'creditDecision', 'netAmount', 'opportunityRecordType', 'goodsDescription'];

  constructor(
    private formBuilder: FormBuilder,
    private toastService: PortalHotToastService,
    private dialogService: ApplicationDialogService,
  ) {
  }

  ngOnInit(): void {
    this.dataSource = this.dedupeByApplicationId(this.duplicatedApplications ?? []);
    this.refreshDisplayedData();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['duplicatedApplications']) return;
    this.dataSource = this.dedupeByApplicationId(this.duplicatedApplications ?? []);
    this.refreshDisplayedData();
  }

  private dedupeByApplicationId(list: DuplicatedApplication[]): DuplicatedApplication[] {
    const seen = new Set<number>();
    const result: DuplicatedApplication[] = [];
    for (const app of list) {
      if (!seen.has(app.ApplicationId)) {
        seen.add(app.ApplicationId);
        result.push(app);
      }
    }
    return result;
  }

  getApplicationCreateTime(app: DuplicatedApplication) {
    return displayApplicationDateTime(moment(app.CreateTime));
  }

  refreshDisplayedData() {
    this.total = this.dataSource.length;
    this.displayedData = this.dataSource;
  }

  getEntityName(app: DuplicatedApplication): string {
    return getCompanyName(app);
  }

  getNetAmount(app: DuplicatedApplication): number {
    return Number(getLoanAmount(app)) || 0;
  }

  getOpportunityRecordType(app: DuplicatedApplication): string {
    switch (app.ApplicationType) {
      case 'AssetFinance': return 'Asset Finance';
      case 'BusinessLoans': return 'Business Loan';
      case 'BusinessOverdraft': return 'Business Overdraft';
      case 'CorporateLoans': return 'Corporate Loan';
      case 'InsurancePremium': return 'Insurance Premium';
      case 'Consumer': return 'Consumer';
      case 'TradeFinance': return 'Trade Finance';
      default: return app.ApplicationType ?? '';
    }
  }

  getGoodsDescription(app: DuplicatedApplication): string {
    return getAssetDescription(app) ?? '';
  }

  onClick(app: DuplicatedApplication) {
    this.clickApplicationEvent.emit(app);
  }
}
